// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TagWrapper, { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import constants from "@store/constants";
import useForceUpdate from "@store/Framework/Common/Hooks/useForceUpdate";
import { convertFileToBase64 } from "@store/Framework/Common/Utilities/convertFileToBase64";
import setFieldValue from "@store/Framework/Store/Greif/Components/RecommendedProductRequestForm/Handlers/SetFieldValue";
import FieldLabelAndTip from "@store/Library/Components/FieldLabelAndTip";
import {
    FileData,
    RecommendedProductRequestFormContext,
} from "@store/Library/Widgets/RecommendedProductRequest/RecommendedProductRequestForm";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    fieldLabel = "fieldLabel",
    fieldSubLabel = "fieldSubLabel",
    isRequired = "isRequired",
    fileImportFormat = "fileImportFormat",
    fileImportMax = "fileImportMax",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isRequired]: boolean;
        [fields.fileImportFormat]: string;
        [fields.fileImportMax]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    attachedFiles: state.greif.components.recommendedProductRequestForm.fieldValues["attachedFiles"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AttachedFilesStyles {
    attachedFilesFileUploadField?: FileUploadPresentationProps;
    attachedFilesWrapper?: InjectableCss;
    attachedFilesTitle?: TypographyPresentationProps;
    tagWrapper?: TagWrapperProps;
    attachedFilerTag?: TagPresentationProps;
}

export const attachedFilesStyles: AttachedFilesStyles = {
    attachedFilesWrapper: {
        css: css`
            margin-top: ${pxToRem(20)};
        `,
    },
    attachedFilesTitle: {
        forwardAs: "p",
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin-bottom: ${pxToRem(5)};
        `,
    },
    attachedFilerTag: {
        css: css`
            ${x => x.theme.tag.defaultProps?.css}
            font-size: ${x => x.theme.design.typography.size.base};
        `,
        iconProps: {
            size: 16,
        },
    },
};

const styles = attachedFilesStyles;

const AttachedFiles: FC<Props> = ({ fields, attachedFiles, setFieldValue }) => {
    const { isRequired, fileImportFormat, fileImportMax } = fields;
    const recommendedProductRequestFormContext = useContext(RecommendedProductRequestFormContext);
    const [attachedFilesErrorMessage, setAttachedFilesErrorMessage] = useState<string>("");
    const [localAttachedFiles, setLocalAttachedFiles] = useState<File[]>([]);
    const [localAttachedFilesData, setLocalAttachedFilesData] = useState<any[]>([]);

    const attachmentLabel = translate("Attachments");
    const attachmentInstructionLabel = translate("e.g. MSDS of your produts for technical advice");

    const forceUpdate = useForceUpdate();

    useEffect(() => {
        recommendedProductRequestFormContext.validators.attachedFiles = validateAttachedFiles;
        if (typeof attachedFiles !== "undefined") {
            validateAttachedFiles();
        }
    }, [attachedFiles]);

    useEffect(() => {
        recommendedProductRequestFormContext.validators.attachedFiles = validateAttachedFiles;
    }, [isRequired]);

    const validateAttachedFiles = () => {
        const errorMessage =
            isRequired && !attachedFiles ? siteMessage("Field_Required", attachmentLabel).toString() : "";
        setAttachedFilesErrorMessage(errorMessage);
        return !errorMessage;
    };

    const readFileAsText = (file: File) => {
        return convertFileToBase64(file);
    };

    const buildAndSetFileObject = () => {
        const sentFiles: FileData[] = [];
        if (localAttachedFiles) {
            for (let i = 0; i < localAttachedFiles.length; i++) {
                const file = localAttachedFiles[i];
                const cleanedFile = localAttachedFilesData[i]
                    .replace("-", "+")
                    .replace("_", "/")
                    .match(`(?<=base64,).*`);
                sentFiles.push({ filename: file.name, data: cleanedFile[0] });
            }
        }

        setFieldValue({ key: "attachedFiles", value: [...sentFiles] });
    };

    const fileUploadHandler: React.ChangeEventHandler<any> = event => {
        event.preventDefault();

        const uploadedFilesLength = Array.from(event.target.files).length;
        const alreadyUploadFilesLength = localAttachedFiles.length;

        if (uploadedFilesLength + alreadyUploadFilesLength > fileImportMax) {
            setAttachedFilesErrorMessage(
                translate(
                    "Please select fewer files or remove files from the list above before adding new ones. Maximum allowed is {0}.",
                    String(fileImportMax),
                ),
            );
            return;
        }
        attachedFilesErrorMessage ? setAttachedFilesErrorMessage("") : null;

        Array.from(event.target.files).forEach((file: any) => {
            localAttachedFiles.push(file);
        });

        if (event.target.files[0] !== null) {
            const readers = [];
            for (let i = 0; i < event.target.files.length; i++) {
                const file = event.target.files[i];
                readers.push(readFileAsText(file));
            }
            Promise.all(readers)
                .then(files => {
                    files.forEach(file => {
                        localAttachedFilesData.push(file);
                    });
                })
                .then(() => {
                    buildAndSetFileObject();
                });
        }

        forceUpdate();
        event.target.value = null;
    };

    const handleDocumentRemove = (fileName: string) => {
        const indexToRemove = localAttachedFiles?.map(i => i.name).indexOf(fileName);
        localAttachedFiles.splice(indexToRemove, 1);
        setLocalAttachedFiles([...localAttachedFiles]);

        localAttachedFilesData.splice(indexToRemove, 1);
        setLocalAttachedFilesData([...localAttachedFilesData]);

        localAttachedFiles.length <= fileImportMax && setAttachedFilesErrorMessage("");

        setFieldValue({ key: "attachedFiles", value: [...localAttachedFiles] });

        forceUpdate();
    };

    return (
        <StyledWrapper>
            <FileUpload
                {...styles.attachedFilesFileUploadField}
                label={<FieldLabelAndTip label={attachmentLabel} optional optionalLabel={attachmentInstructionLabel} />}
                onChange={fileUploadHandler}
                accept={fileImportFormat}
                disabled={localAttachedFiles.length >= fields.fileImportMax}
                multiple
                error={attachedFilesErrorMessage}
            />
            {localAttachedFiles.length > 0 && (
                <StyledWrapper {...styles.attachedFilesWrapper}>
                    <Typography {...styles.attachedFilesTitle}>{translate("Attached files")}</Typography>
                    <TagWrapper {...styles.tagWrapper}>
                        {localAttachedFiles.map((file, index) => (
                            <Tag
                                key={index}
                                {...styles.attachedFilerTag}
                                onDelete={() => handleDocumentRemove(file.name)}
                            >
                                {`${file.name}`}
                            </Tag>
                        ))}
                    </TagWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AttachedFiles),
    definition: {
        displayName: "Attachments",
        group: constants.widgetGroup.recommendedProductRequest,
        icon: "input-pipe",
        allowedContexts: [constants.pageType.recommendedProductRequest],
        fieldDefinitions: [
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.fileImportMax,
                displayName: "Maximum of files allowed",
                fieldType: "General",
                editorTemplate: "IntegerField",
                defaultValue: 5,
            },
            {
                name: fields.fileImportFormat,
                displayName: "Allowed File Formats",
                fieldType: "General",
                editorTemplate: "TextField",
                tooltip: "Use comma-separated values. i.e.: .pdf, .zip",
                defaultValue: ".pdf, .zip",
            },
        ],
    },
};

export default widgetModule;
